import { ChangeEvent, FC, MouseEvent, useMemo, useState } from "react";
import {
  Box,
  InputAdornment,
  makeStyles,
  MenuItem,
  Paper,
  TextField,
} from "@material-ui/core";
import {
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
} from "@material-ui/icons";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { find, uniq } from "lodash";
import { usePostHog } from "posthog-js/react";

import { UiParam, UiParams } from "hooks/useQueryParams/types";
import { EXCHANGE_FILTER_SORT } from "services/postHog/constants";
import theme from "theme";
import { menuItemPurpleStyles } from "theme/constants";

const useStyles = makeStyles(theme => ({
  paper: {
    borderRadius: theme.custom.shape.borderRadiusSoft,
  },
  dropdown: {
    borderRadius: theme.custom.shape.borderRadiusSoft,
    backgroundColor: theme.palette.background.default,
    "& .MuiInputBase-root": {
      minHeight: "45px",
    },
    "& .MuiInputBase-input": {
      color: theme.custom.palette.gray.darker,
      fontWeight: theme.typography.fontWeightMedium,
      marginLeft: "-4px",
    },
    "& .MuiInput-underline:before": {
      borderBottom: "none",
    },
    "& .MuiInput-underline:after": {
      borderBottom: "none",
    },
    "& .MuiInput-underline:hover:not(.Mui-disabled)::before": {
      borderBottom: "none",
    },
    "& .MuiSelect-select.MuiSelect-select:focus": {
      backgroundColor: "unset",
    },
  },
  dropdownMenuItem: menuItemPurpleStyles,
  dropdownInputAdornment: {
    paddingLeft: theme.spacing(2),
    marginBottom: "1px",
    "&:hover": {
      cursor: "pointer",
    },
  },
  toggleButtonGroup: {
    borderRadius: theme.custom.shape.borderRadiusSoft,
    "& .MuiToggleButton-root": {
      borderRadius: theme.custom.shape.borderRadiusSoft,
    },
    "& .MuiToggleButtonGroup-groupedHorizontal:not(:last-child)": {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },
    "& .MuiToggleButtonGroup-groupedHorizontal:not(:first-child)": {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
    "& .Mui-selected": {
      backgroundColor: theme.custom.palette.purple.main,
      color: theme.custom.palette.gray.light,
    },
    "& .MuiToggleButton-root:hover": {
      backgroundColor: theme.custom.palette.purple.light,
    },
  },
  toggleButton: {
    height: "45px",
  },
}));

const makePostHogSortName = (name: string, direction: "asc" | "desc") =>
  `${direction === "desc" ? "Highest" : "Lowest"} ${name}`;

interface SortControlsProps {
  uiParams: UiParams;
  setUiParams: (setState: (uiParams: UiParams) => UiParams) => void;
  isAuthenticated: boolean;
  validUiSortOptions: UiParam[];
}

const SortControls: FC<SortControlsProps> = ({
  uiParams,
  setUiParams,
  isAuthenticated,
  validUiSortOptions,
}) => {
  const classes = useStyles(theme);
  const posthog = usePostHog();

  const [isSelectOpen, setIsSelectOpen] = useState(false);

  const [availableSortOptions, uniqueSortOptionNames] = useMemo(() => {
    const availableSortOptions = isAuthenticated
      ? validUiSortOptions
      : validUiSortOptions.filter(option => !option.requiresLogin);
    const uniqueSortOptionNames = uniq(
      availableSortOptions.map(option => option.name),
    );
    return [availableSortOptions, uniqueSortOptionNames];
  }, [isAuthenticated, validUiSortOptions]);

  const handleSortOptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIsSelectOpen(false);
    const sortOption = find(
      availableSortOptions,
      option => option.name === event.target.value,
    );
    if (sortOption) {
      posthog?.capture(EXCHANGE_FILTER_SORT, {
        actionType: "sort",
        filterBy: makePostHogSortName(sortOption.name, sortOption.direction),
      });
      setUiParams(prev => ({
        ...prev,
        page: { ...prev.page, value: "1" },
        sort: sortOption,
      }));
    }
  };

  const handleSortDirectionChange = (
    _event: MouseEvent<HTMLElement>,
    value: string,
  ) => {
    const sortOption = find(
      availableSortOptions,
      option =>
        option.name === uiParams.sort.name && option.direction === value,
    );
    if (sortOption) {
      posthog?.capture(EXCHANGE_FILTER_SORT, {
        actionType: "sort",
        filterBy: makePostHogSortName(sortOption.name, sortOption.direction),
      });
      setUiParams(prev => ({
        ...prev,
        page: { ...prev.page, value: "1" },
        sort: sortOption,
      }));
    }
  };

  return (
    <Box display="flex" alignItems="center" data-testid="sort-controls">
      <Box width="100%" mr={1} minWidth="180px">
        <Paper elevation={3} className={classes.paper}>
          <TextField
            select
            fullWidth
            value={uiParams.sort.name}
            onChange={handleSortOptionChange}
            className={classes.dropdown}
            onClick={() => setIsSelectOpen(!isSelectOpen)}
            SelectProps={{
              open: isSelectOpen, // control this state so that clicking the InputAdornment can open the select menu
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment
                  className={classes.dropdownInputAdornment}
                  position="start"
                  onClick={() => setIsSelectOpen(!isSelectOpen)}
                >
                  Sort by
                </InputAdornment>
              ),
            }}
          >
            {uniqueSortOptionNames.map(sortName => (
              <MenuItem
                className={classes.dropdownMenuItem}
                key={sortName}
                value={sortName}
              >
                {sortName}
              </MenuItem>
            ))}
          </TextField>
        </Paper>
      </Box>
      <Paper elevation={3} className={classes.paper}>
        <ToggleButtonGroup
          className={classes.toggleButtonGroup}
          value={uiParams.sort.direction}
          exclusive
          onChange={handleSortDirectionChange}
          size="small"
          aria-label="sort direction"
        >
          <ToggleButton
            className={classes.toggleButton}
            value="asc"
            disabled={uiParams.sort.direction === "asc"}
            aria-label="sort ascending"
          >
            <ArrowUpwardIcon />
          </ToggleButton>
          <ToggleButton
            className={classes.toggleButton}
            value="desc"
            disabled={uiParams.sort.direction === "desc"}
            aria-label="sort descending"
          >
            <ArrowDownwardIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Paper>
    </Box>
  );
};

export default SortControls;
